import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Container } from "reactstrap";
import { useSelector } from "react-redux";
import Breadcrumb from "../common/breadcrumb";
import { C, tbl, reportingGlobalCSS } from "../reporting/reportingStyles";
import { Tile, RCard, Badge, Loader } from "../reporting/ReportingShared";
import { getMembershipTypes, setMembershipTypeActive } from "../../utils/membershipApi";
import { LOYALTY_POLICIES, LOYALTY_POLICY_BADGES } from "./membershipConstants";
import { getMerchantListing } from "../../utils/apiCalls";
import { successToast } from "../../shared/Toast";

/**
 * Membership tiers for one merchant.
 *
 * Mirrors merchant/loyalty-list.js — same reporting shell, same merchant-scoping ladder.
 *
 * The one thing that differs on purpose: there is no delete. Retiring a tier means
 * deactivating it, which stops the discount for everyone and hides it in the app while
 * leaving the row in place. Two things point at that row — the usage ledger that
 * attributes historical spend tier by tier, and every order ever placed under it — so a
 * delete would orphan both. The foreign keys are RESTRICT, so the database refuses one
 * anyway; not offering the button is how the UI stops promising something it cannot do.
 */

const buttonStyle = {
    padding: "10px 18px", borderRadius: 10, fontSize: 13, fontWeight: 700,
    border: "none", cursor: "pointer", transition: "all 0.15s",
    background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`, color: C.white,
    boxShadow: "0 4px 12px rgba(0, 150, 137,0.25)", textDecoration: "none",
    display: "inline-flex", alignItems: "center", gap: 6,
};

const smallButton = {
    padding: "6px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600,
    border: `1px solid ${C.border}`, cursor: "pointer", color: C.textMed, background: C.white,
    display: "inline-flex", alignItems: "center", gap: 6,
};

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : null);

/** "15%" or "SAR 20" — the two discount shapes read very differently at a glance. */
export const formatDiscount = (type) =>
    type?.discountType === "amount"
        ? `SAR ${Number(type?.discountValue ?? 0).toFixed(2)}`
        : `${Number(type?.discountValue ?? 0)}%`;

/**
 * A tier can be inactive for two quite different reasons, and an admin needs to tell them
 * apart: switched off by hand, or inside/outside its own effective window. A tier that
 * looks active but is refusing discounts because its window has not opened yet is the
 * confusing case the API reports as `type_window`.
 */
const tierState = (t) => {
    if (!t?.isActive) return { label: "● Inactive", color: C.coral, bg: C.coralLight };

    const now = Date.now();
    if (t.effectiveDate && now < new Date(t.effectiveDate).getTime()) {
        return { label: `◷ Starts ${fmtDate(t.effectiveDate)}`, color: C.orange, bg: C.orangeLight };
    }
    if (t.expiryDate && now > new Date(t.expiryDate).getTime()) {
        return { label: `◷ Ended ${fmtDate(t.expiryDate)}`, color: C.orange, bg: C.orangeLight };
    }
    return { label: "● Active", color: C.green, bg: C.greenLight };
};

const MembershipTypeList = () => {
    const navigate = useNavigate();
    const userType = localStorage.getItem("userType");
    const merchantID = localStorage.getItem("MerchantId");
    const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
    const selectedMerchant = userType === "merchant" ? merchantID : reduxSelectedMerchant;

    const [types, setTypes] = useState([]);
    const [merchantList, setMerchantList] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [busyId, setBusyId] = useState(null);

    // The API refuses an unscoped list — a tier carries a discount percentage, so listing
    // every merchant's would leak commercial terms. Resolve the merchant first, and say so
    // in the UI rather than firing a request that can only 400.
    const merchantFilter = userType === "merchant"
        ? merchantID
        : (selectedMerchant && selectedMerchant !== "all") ? selectedMerchant : null;

    const fetchData = useCallback(async () => {
        if (!merchantFilter) { setTypes([]); return; }
        setIsLoading(true);
        setTypes(await getMembershipTypes({ merchantId: merchantFilter }) || []);
        setIsLoading(false);
    }, [merchantFilter]);

    useEffect(() => { fetchData(); }, [fetchData]);

    useEffect(() => {
        if (userType !== "merchant") {
            (async () => setMerchantList(await getMerchantListing()))();
        }
    }, [userType]);

    const tiles = useMemo(() => {
        const active = types.filter((t) => t.isActive);
        return {
            total: types.length,
            active: active.length,
            members: types.reduce((sum, t) => sum + (Number(t.activeMembers) || 0), 0),
            capped: types.filter((t) =>
                t.maxDailyOrders != null || t.maxMonthlyOrders != null || t.maxDailyDiscountValue != null
            ).length,
        };
    }, [types]);

    const toggleActive = async (type) => {
        const next = !type.isActive;
        const warning = next
            ? `Activate "${type.name}"? Members on this tier will start receiving ${formatDiscount(type)} off.`
            : `Deactivate "${type.name}"? Its ${type.activeMembers} member(s) stop receiving the discount immediately and the tier disappears from the app. Nothing is deleted — past orders and the usage ledger keep their history, and you can reactivate it later.`;
        if (!window.confirm(warning)) return;

        setBusyId(type.id);
        const updated = await setMembershipTypeActive(type.id, next);
        setBusyId(null);
        if (updated) {
            successToast(next ? "Tier activated" : "Tier deactivated");
            fetchData();
        }
    };

    const merchantNameById = (id) => merchantList.find((m) => m.id === id)?.name || "—";

    return (
        <Fragment>
            <Breadcrumb title="Membership Types" parent="Membership" />
            <Container fluid>
                <style>{reportingGlobalCSS}</style>
                <div className="rpt-wrap">

                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, marginBottom: 20 }}>
                        <Tile label="Total Tiers" value={tiles.total} sub={`${tiles.active} active`} icon="🎖️" gradient={`linear-gradient(135deg, ${C.coral}, #00bfa5)`} delay={0} />
                        <Tile label="Active Members" value={tiles.members} sub="Across all tiers" icon="👥" gradient={`linear-gradient(135deg, ${C.blue}, #A8B8FF)`} delay={80} />
                        <Tile label="Tiers With Caps" value={tiles.capped} sub="Daily or monthly limits" icon="🛡️" gradient={`linear-gradient(135deg, ${C.orange}, #FFB347)`} delay={160} />
                        <Tile label="Live Now" value={tiles.active} sub={tiles.total > 0 ? `${Math.round((tiles.active / tiles.total) * 100)}% of tiers` : "No tiers yet"} icon="✓" gradient={`linear-gradient(135deg, ${C.green}, #38F9D7)`} delay={240} />
                    </div>

                    <RCard
                        title={
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                                <span>🎖️ Membership Types <span style={{ color: C.textLight, fontWeight: 500, marginLeft: 8 }}>· {tiles.total} total</span></span>
                                {merchantFilter && <Link to="/membership/types/add" style={buttonStyle}>+ Create Tier</Link>}
                            </div>
                        }
                    >
                        {!merchantFilter ? (
                            <div style={{ textAlign: "center", padding: 40, color: C.textMed }}>
                                Select a merchant to see its membership tiers.
                            </div>
                        ) : isLoading ? (
                            <Loader />
                        ) : types.length === 0 ? (
                            <div style={{ textAlign: "center", padding: 40, color: C.textMed }}>
                                No membership tiers yet. Click <strong>+ Create Tier</strong> to add one.
                            </div>
                        ) : (
                            <div style={{ overflowX: "auto" }}>
                                <table style={tbl.table}>
                                    <thead>
                                        <tr>
                                            <th style={tbl.th}>Tier</th>
                                            <th style={tbl.th}>Discount</th>
                                            <th style={tbl.th}>Members</th>
                                            <th style={tbl.th}>Caps</th>
                                            <th style={tbl.th}>Scope</th>
                                            <th style={tbl.th}>Points</th>
                                            {userType !== "merchant" && <th style={tbl.th}>Merchant</th>}
                                            <th style={tbl.th}>Status</th>
                                            <th style={tbl.th}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {types.map((t) => {
                                            const state = tierState(t);
                                            const caps = [
                                                t.maxDailyOrders != null && `${t.maxDailyOrders}/day`,
                                                t.maxMonthlyOrders != null && `${t.maxMonthlyOrders}/mo`,
                                                t.maxDailyDiscountValue != null && `SAR ${t.maxDailyDiscountValue}/day`,
                                                t.maxOrderValue != null && `max SAR ${t.maxOrderValue}`,
                                            ].filter(Boolean);
                                            const scope = [
                                                t.applyToAllCategories === false && `${t.eligibleCategories?.length || 0} categories`,
                                                t.excludedProducts?.length > 0 && `${t.excludedProducts.length} excluded`,
                                                t.eligibleBranches?.length > 0 && `${t.eligibleBranches.length} branches`,
                                            ].filter(Boolean);

                                            return (
                                                <tr key={t.id} className="rpt-row">
                                                    <td style={{ ...tbl.td, fontWeight: 600, color: C.text }}>
                                                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                            <span style={{
                                                                width: 10, height: 10, borderRadius: 3, flexShrink: 0,
                                                                background: t.cardColour || C.textLight,
                                                                border: `1px solid ${C.border}`,
                                                            }} />
                                                            <div>
                                                                {t.name || "—"}
                                                                {t.arabicName && <div style={{ fontSize: 11, color: C.textLight, fontWeight: 400 }}>{t.arabicName}</div>}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td style={{ ...tbl.td, fontWeight: 600 }}>
                                                        {formatDiscount(t)}
                                                        {(t.discountRules || []).length > 0 && (
                                                            // The figure above is only the default once rates exist.
                                                            <div style={{ fontSize: 11, fontWeight: 500, color: C.textMed }}>
                                                                default · {t.discountRules.length} custom rate{t.discountRules.length === 1 ? "" : "s"}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td style={tbl.td}>{t.activeMembers ?? 0}</td>
                                                    <td style={{ ...tbl.td, color: C.textMed, fontSize: 12 }}>
                                                        {caps.length ? caps.join(" · ") : <span style={{ color: C.textLight }}>No caps</span>}
                                                    </td>
                                                    <td style={{ ...tbl.td, color: C.textMed, fontSize: 12 }}>
                                                        {/* Empty eligibility means "everything" throughout the API, so the
                                                            honest label here is "All items", never a blank cell. */}
                                                        {scope.length ? scope.join(" · ") : <span style={{ color: C.textLight }}>All items, all branches</span>}
                                                    </td>
                                                    <td style={tbl.td}>
                                                        {(() => {
                                                            // Named after the policy rather than reduced to "points / no
                                                            // points": three of the four values DO earn in some situation,
                                                            // so a yes/no badge would state the opposite of the truth for
                                                            // two of them.
                                                            const policy = t.memberLoyaltyPolicy || "when_no_discount";
                                                            const badge = LOYALTY_POLICY_BADGES[policy]
                                                                || { text: policy, tone: "muted" };
                                                            const good = badge.tone === "good";
                                                            const spec = LOYALTY_POLICIES.find((p) => p.value === policy);
                                                            return (
                                                                <span title={spec ? `${spec.label} — ${spec.summary}` : policy}>
                                                                    <Badge
                                                                        color={good ? C.green : C.textMed}
                                                                        bg={good ? C.greenLight : C.borderLight}
                                                                    >
                                                                        {badge.text}
                                                                    </Badge>
                                                                </span>
                                                            );
                                                        })()}
                                                    </td>
                                                    {userType !== "merchant" && (
                                                        <td style={{ ...tbl.td, color: C.textMed }}>{merchantNameById(t.merchantId)}</td>
                                                    )}
                                                    <td style={tbl.td}>
                                                        <Badge color={state.color} bg={state.bg}>{state.label}</Badge>
                                                    </td>
                                                    <td style={tbl.td}>
                                                        <div style={{ display: "flex", gap: 6 }}>
                                                            <button
                                                                onClick={() => navigate(`/membership/types/edit/${t.id}`)}
                                                                style={smallButton}
                                                            >
                                                                ✏️ Edit
                                                            </button>
                                                            <button
                                                                onClick={() => toggleActive(t)}
                                                                disabled={busyId === t.id}
                                                                style={{
                                                                    ...smallButton,
                                                                    color: t.isActive ? C.coral : C.green,
                                                                    opacity: busyId === t.id ? 0.5 : 1,
                                                                }}
                                                            >
                                                                {t.isActive ? "⏸ Deactivate" : "▶ Activate"}
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </RCard>
                </div>
            </Container>
        </Fragment>
    );
};

export default MembershipTypeList;
